import { isoDate as melDate } from "@/lib/intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  templateKey: "", mode: "blank", includeIds: false, appointmentId: "",
  sourceId: "", resultId: "", writing: "compact", regions: [],
};

export async function errorText(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).detail || e.message; } catch { return e.message; }
  }
  const d = data?.detail;
  return typeof d === "string" ? d : (d ? JSON.stringify(d) : e?.message || "Something went wrong");
}

const isoDay = (n) => melDate(new Date(Date.now() + n * 86400000));

function useMeta(open, assessmentId, clientEmail, defaultTemplateKey, reset) {
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setMeta(null); setMetaError("");
    const params = assessmentId ? { assessment_id: assessmentId }
      : { client_email: clientEmail || undefined, preferred_template_key: defaultTemplateKey || undefined };
    api.get("/assessment-print/templates", { params })
      .then((r) => {
        if (!alive) return;
        const d = r.data;
        const a = d.assessment;
        const known = d.templates.some((t) => t.key === defaultTemplateKey);
        setMeta(d);
        reset({
          templateKey: a?.template_key || d.suggested_template_key || (known ? defaultTemplateKey : "core"),
          appointmentId: a?.appointment_id ? String(a.appointment_id) : "",
        });
      })
      .catch(async (e) => { const m = await errorText(e); if (alive) setMetaError(m); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assessmentId]);
  return { meta, metaError };
}

function useAppointments(open, email) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open || !email) { setRows([]); return; }
    api.get("/appointments", { params: {
      email, minDate: isoDay(-30), maxDate: isoDay(60), max: 50, enrich_price: false, enrich_intake: false,
    } })
      .then((r) => setRows((r.data || []).filter((a) => !a.canceled)))
      .catch(() => setRows([]));
  }, [open, email]);
  return rows;
}

function useSources(open, email, s, assessmentId, setS) {
  const [sources, setSources] = useState(null);
  useEffect(() => {
    if (!open || !email || !s.templateKey) { setSources(null); return; }
    let alive = true;
    setSources(null);
    api.get("/assessment-print/sources", { params: {
      template_key: s.templateKey, client_email: email,
      assessment_id: assessmentId || undefined, appointment_id: s.appointmentId || undefined,
    } })
      .then((r) => {
        if (!alive) return;
        setSources(r.data);
        setS((p) => {
          const still = r.data.sources.some((x) => x.eligible && x.id === p.sourceId);
          return still ? p : { ...p, sourceId: r.data.suggested_id || "" };
        });
      })
      .catch(async (e) => alive && setSources({ sources: [], disabled_reason: await errorText(e) }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, email, s.templateKey, s.appointmentId, assessmentId]);
  return sources;
}

function useResults(open, email, s, assessmentId, setS) {
  const [results, setResults] = useState(null);
  const active = open && !!email && !!s.templateKey && s.mode === "onboarding_prefill";
  useEffect(() => {
    if (!active) { setResults(null); return; }
    let alive = true;
    api.get("/assessment-print/results", { params: {
      template_key: s.templateKey, client_email: email, assessment_id: assessmentId || undefined,
    } })
      .then((r) => {
        if (!alive) return;
        setResults(r.data);
        setS((p) => (r.data.results.some((x) => x.eligible && x.id === p.resultId) ? p : { ...p, resultId: "" }));
      })
      .catch(() => alive && setResults(null));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, email, s.templateKey, assessmentId]);
  return results;
}

export function usePrintForm({ open, assessmentId, clientEmail, clientName, canonicalClientId, defaultTemplateKey }) {
  const [s, setS] = useState(DEFAULTS);
  const [exp, setExp] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const urlRef = useRef(null);
  const reset = useCallback((patch) => setS({ ...DEFAULTS, ...patch }), []);
  const { meta, metaError } = useMeta(open, assessmentId, clientEmail, defaultTemplateKey, reset);
  const ctx = meta?.assessment;
  const email = ctx?.client_email || clientEmail || "";
  const appointments = useAppointments(open, email);
  const sources = useSources(open, email, s, assessmentId, setS);
  const results = useResults(open, email, s, assessmentId, setS);
  const ctxApplies = !!ctx && s.templateKey === ctx.template_key;
  const identifiers = s.mode !== "blank" || s.includeIds;

  const clearPdf = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setPdfUrl(null);
  }, []);
  useEffect(() => { setExp(null); clearPdf(); setError(""); }, [s, clearPdf]);
  useEffect(() => { if (!open) clearPdf(); }, [open, clearPdf]);

  const update = (patch) => setS((p) => {
    const next = { ...p, ...patch };
    return next.mode === "completed" && ctx ? { ...next, templateKey: ctx.template_key, regions: [] } : next;
  });

  const blocker = !s.templateKey ? "Choose an assessment template."
    : s.mode === "completed" && ctx?.status !== "finalised" ? "Finalise to print the completed record."
    : s.mode === "onboarding_prefill" && !email ? "Open this from a client to pre-fill from onboarding."
    : s.mode === "onboarding_prefill" && sources?.disabled_reason ? sources.disabled_reason
    : s.mode === "onboarding_prefill" && !s.sourceId ? "Choose which submitted onboarding form to use."
    : null;

  const body = () => ({
    mode: s.mode, template_key: s.templateKey, assessment_id: assessmentId || undefined,
    client_email: email || undefined, client_name: ctx?.client_name || clientName || undefined,
    canonical_client_id: ctx?.canonical_client_id || canonicalClientId || undefined,
    appointment_id: identifiers && s.appointmentId ? s.appointmentId : undefined,
    onboarding_submission_id: s.mode === "onboarding_prefill" ? s.sourceId : undefined,
    result_id: s.mode === "onboarding_prefill" && s.resultId ? s.resultId : undefined,
    include_patient_identifiers: s.mode === "blank" && s.includeIds,
    writing_space: s.writing, regions: ctxApplies ? [] : s.regions,
  });

  async function ensureExport() {
    if (exp) return exp;
    const r = await api.post("/assessment-print/exports", body());
    setExp(r.data);
    return r.data;
  }
  const fetchPdf = async (id, action) => (await api.get(`/assessment-print/exports/${id}/pdf`, {
    params: { action }, responseType: "blob" })).data;

  async function run(kind, fn) {
    setBusy(kind); setError("");
    try { await fn(); } catch (e) { setError(await errorText(e)); } finally { setBusy(null); }
  }
  const preview = () => run("preview", async () => {
    const e = await ensureExport();
    const blob = await fetchPdf(e.id, "preview");
    clearPdf();
    urlRef.current = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    setPdfUrl(urlRef.current);
  });
  const download = () => run("download", async () => {
    const e = await ensureExport();
    const url = URL.createObjectURL(await fetchPdf(e.id, "download"));
    const a = document.createElement("a");
    a.href = url; a.download = e.filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  const requestPrint = (frame) => run("print", async () => {
    await api.post(`/assessment-print/exports/${exp.id}/print-request`);
    frame?.contentWindow?.focus();
    frame?.contentWindow?.print();
  });

  return {
    s, update, meta, metaError, ctx, ctxApplies, email, appointments, sources, results, identifiers,
    exp, pdfUrl, busy, error, blocker, preview, download, requestPrint,
  };
}
